import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import { listClientVendors, createClientVendor } from "../../api/clientVendorsApi";
import { getClientPortalView } from "../../api/clientPortalApi";
import VendorFormModal, { EMPTY_VENDOR_FORM } from "../../components/clientPortal/VendorFormModal";
import ClientSummaryMetrics from "../../components/clientPortal/ClientSummaryMetrics";
import {
  ClientPortalEmpty,
  ClientPortalError,
  ClientPortalLoading,
} from "../../components/clientPortal/ClientPortalStates";
import { stripVendorImportNotes } from "../../clientViewRuntime/utils/golfHomesVendorDisplay";
import { formatStatusLabel } from "../../utils/clientPortalFormat";
import {
  formatWebsiteLabel,
  vendorCompanyLine,
  vendorDisplayName,
  vendorInitials,
  vendorTypeColor,
  vendorTypeIcon,
} from "../../utils/clientVendorUi";
import { resolveClientPortalAssetUrl } from "../../api/clientUploadApi";

const ClientRealtorsVendorsPage = () => {
  const { clientViewId } = useParams();
  const [viewName, setViewName] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [search, setSearch] = useState("");
  const [vendorType, setVendorType] = useState("");
  const [page, setPage] = useState(1);
  const paths = useClientPortalPaths();

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_VENDOR_FORM);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [viewBody, listBody] = await Promise.all([
        getClientPortalView(clientViewId),
        listClientVendors(clientViewId, {
          page,
          pageSize: 24,
          search: search || undefined,
          vendorType: vendorType || undefined,
        }),
      ]);
      setViewName(viewBody.clientView?.name || "Client View");
      setData(listBody);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load vendors. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId, page, search, vendorType]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setCreateSaving(true);
    setCreateError("");
    try {
      await createClientVendor(clientViewId, createForm);
      setCreateOpen(false);
      setCreateForm(EMPTY_VENDOR_FORM);
      setSuccessMessage("Professional added successfully.");
      load();
    } catch (error) {
      setCreateError(error.message || "Unable to create vendor.");
    } finally {
      setCreateSaving(false);
    }
  };

  const summary = data?.summary;
  const summaryMetrics = summary
    ? [
        { label: "Total", value: summary.total },
        { label: "Realtors / Brokers", value: summary.realtors },
        { label: "Service Vendors", value: summary.vendors },
        { label: "Active", value: summary.active },
      ]
    : [];

  const vendors = data?.vendors || [];
  const typeChips = useMemo(() => {
    const options = data?.filterOptions?.vendorTypes || [];
    return options.filter((opt) => opt.count > 0);
  }, [data?.filterOptions?.vendorTypes]);

  const hasActiveFilters = Boolean(search.trim() || vendorType);
  const detailBase = paths.viewSection(clientViewId, "realtors-vendors");

  return (
    <div className="cp-vendors-page">
      <header className="cp-vendors-page-header panel-card">
        <p className="eyebrow">
          <Link to={paths.view(clientViewId)}>{viewName}</Link> / Realtors &amp; Vendors
        </p>
        <div className="cp-vendors-page-header-row">
          <div>
            <h2>Realtors &amp; Vendors</h2>
            <p className="muted cp-vendors-page-lead">
              Browse realtors, brokers, and service providers for this client view. Select a card to
              open their full profile.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setCreateError("");
              setCreateOpen(true);
            }}
          >
            + Add Professional
          </button>
        </div>
      </header>

      {successMessage ? (
        <section className="panel-card client-vendor-toast cp-vendors-toast">
          <p className="muted">
            {successMessage}{" "}
            <button type="button" className="cv-button-link" onClick={() => setSuccessMessage("")}>
              Dismiss
            </button>
          </p>
        </section>
      ) : null}

      {loading && !data ? <ClientPortalLoading message="Loading professionals…" /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} /> : null}

      {data ? (
        <>
          <ClientSummaryMetrics metrics={summaryMetrics} />

          <section className="panel-card cp-vendors-toolbar">
            <div className="cp-vendors-search-row">
              <label className="cp-vendors-search-field">
                <span className="visually-hidden">Search</span>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => {
                    setPage(1);
                    setSearch(e.target.value);
                  }}
                  placeholder="Search by name, email, or phone…"
                />
              </label>
              <select
                className="cp-vendors-type-select"
                value={vendorType}
                onChange={(e) => {
                  setPage(1);
                  setVendorType(e.target.value);
                }}
                aria-label="Filter by type"
              >
                <option value="">All types</option>
                {(data.filterOptions?.vendorTypes || []).map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label} ({opt.count})
                  </option>
                ))}
              </select>
              {hasActiveFilters ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setSearch("");
                    setVendorType("");
                    setPage(1);
                  }}
                >
                  Clear filters
                </button>
              ) : null}
            </div>

            {typeChips.length > 0 ? (
              <div className="cp-vendors-type-chips" role="group" aria-label="Filter by professional type">
                {typeChips.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`cp-vendors-chip${vendorType === opt.key ? " cp-vendors-chip--active" : ""}`}
                    style={{ "--chip-color": vendorTypeColor(opt.key) }}
                    onClick={() => {
                      setVendorType(vendorType === opt.key ? "" : opt.key);
                      setPage(1);
                    }}
                  >
                    <span aria-hidden="true">{vendorTypeIcon(opt.key)}</span>
                    {opt.label}
                    <span className="cp-vendors-chip-count">{opt.count}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </section>

          {loading ? <ClientPortalLoading message="Updating list…" /> : null}

          {!loading && vendors.length === 0 ? (
            <ClientPortalEmpty
              title="No realtors or vendors yet"
              message={
                hasActiveFilters
                  ? "Try adjusting your search or filters."
                  : "Add your first realtor or vendor to get started."
              }
            />
          ) : null}

          {!loading && vendors.length > 0 ? (
            <div className="cp-vendors-grid">
              {vendors.map((vendor) => (
                <VendorListCard
                  key={vendor.vendorId}
                  vendor={vendor}
                  detailPath={`${detailBase}/${vendor.vendorId}`}
                />
              ))}
            </div>
          ) : null}

          {!loading && data.pagination?.totalPages > 1 ? (
            <div className="client-portal-pagination cp-vendors-pagination">
              <button
                type="button"
                className="cv-button-link"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <span className="muted">
                Page {data.pagination.page} of {data.pagination.totalPages}
              </span>
              <button
                type="button"
                className="cv-button-link"
                disabled={page >= data.pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      {createOpen ? (
        <VendorFormModal
          title="Add Professional"
          form={createForm}
          setForm={setCreateForm}
          onSubmit={handleCreateSubmit}
          onClose={() => setCreateOpen(false)}
          saving={createSaving}
          error={createError}
          submitLabel="Add Professional"
          clientViewId={clientViewId}
        />
      ) : null}
    </div>
  );
};

function VendorListCard({ vendor, detailPath }) {
  const displayName = vendorDisplayName(vendor);
  const company = vendorCompanyLine(vendor);
  const publicNotes = stripVendorImportNotes(vendor.notes);
  const typeColor = vendorTypeColor(vendor.vendorType);
  const photoStyle = vendor.photoUrl
    ? { backgroundImage: `url('${resolveClientPortalAssetUrl(vendor.photoUrl).replace(/'/g, "%27")}')` }
    : undefined;

  return (
    <Link to={detailPath} className="cp-vendor-card">
      <div
        className={`cp-vendor-card-photo${vendor.photoUrl ? "" : " cp-vendor-card-photo--placeholder"}`}
        style={photoStyle}
      >
        {!vendor.photoUrl ? (
          <span className="cp-vendor-card-initials" aria-hidden="true">
            {vendorInitials(displayName)}
          </span>
        ) : null}
      </div>
      <div className="cp-vendor-card-body">
        <div className="cp-vendor-card-meta">
          <span
            className="cp-vendor-type-pill cp-vendor-type-pill--sm"
            style={{ "--vendor-type-color": typeColor }}
          >
            <span aria-hidden="true">{vendorTypeIcon(vendor.vendorType)}</span>
            {vendor.vendorType || "Other"}
          </span>
          <span className={`status-badge status-badge--${vendor.status || "active"}`}>
            {formatStatusLabel(vendor.status)}
          </span>
        </div>
        <h3 className="cp-vendor-card-name">{displayName}</h3>
        {company ? <p className="cp-vendor-card-company">{company}</p> : null}
        <ul className="cp-vendor-card-contact">
          {vendor.email ? <li>{vendor.email}</li> : null}
          {vendor.phone ? <li>{vendor.phone}</li> : null}
          {vendor.website ? <li>{formatWebsiteLabel(vendor.website)}</li> : null}
        </ul>
        {publicNotes ? <p className="cp-vendor-card-notes">{publicNotes}</p> : null}
        <span className="cp-vendor-card-cta">View profile →</span>
      </div>
    </Link>
  );
}

export default ClientRealtorsVendorsPage;
